import api from './api';
import type { Block } from '../components/Courses/editors/BlockEditor/types';

/** Tranche B2 (#489): typed client for the KI-Generator endpoints (content + course). */

export type GeneratedContentType = 'multiple_choice' | 'true_false' | 'free_text_question' | 'summary' | 'key_points' | 'lesson_draft' | 'flashcards';
export type JobStatus = 'pending' | 'processing' | 'completed' | 'failed';
export type SourceType = 'plain_text' | 'url' | 'existing_course' | 'pdf' | 'docx' | 'pptx';

export const CONTENT_TYPES: GeneratedContentType[] = ['lesson_draft', 'summary', 'key_points', 'flashcards', 'multiple_choice', 'true_false', 'free_text_question'];

export interface GeneratedContent {
    id: number;
    type: GeneratedContentType;
    content: Record<string, unknown>;
    status: 'pending' | 'accepted' | 'rejected';
    model: string | null;
    sources?: { ref: string; usage: string | null; sourceId: number; chunkId: number; citation: string }[];
    imported_lesson_id: number | null;
    created_at: string;
}

export interface ContentJob {
    id: number;
    status: JobStatus;
    source_type: SourceType;
    source_course_id: number | null;
    course_context_id: number | null;
    target_types: GeneratedContentType[];
    language: string;
    difficulty_level: string | null;
    error_message: string | null;
    /** What the job was about: a short excerpt in the list, the full text in the detail (#526). */
    source_excerpt?: string | null;
    source_data?: string;
    created_at: string;
    completed_at: string | null;
    content_count: number;
    contents?: GeneratedContent[];
}

export interface CourseJob {
    id: number;
    topic: string;
    goal: string;
    catalog_id: number;
    difficulty: string | null;
    language: string;
    has_source_material: boolean;
    status: JobStatus;
    progress_label: string;
    total_lessons: number;
    completed_lessons: number;
    course_id: number | null;
    course_title: string | null;
    models_used: string[];
    error_message: string | null;
    created_by: number | null;
    created_at: string;
    completed_at: string | null;
}

export interface QuotaUsage {
    course: { used: number; limit: number };
    content: { used: number; limit: number };
    variant: { used: number; limit: number };
}

export interface CreateContentJobInput {
    source_type: SourceType;
    source_text?: string;
    source_url?: string;
    source_course_id?: number;
    course_context_id?: number;
    target_types: GeneratedContentType[];
    language: string;
    difficulty_level?: string | null;
    use_knowledge_base?: boolean;
    knowledge_source_ids?: number[] | null;
}

export interface CreateCourseJobInput {
    topic: string;
    goal: string;
    catalog_id: number;
    difficulty?: string | null;
    language: string;
    source_material?: string;
    use_knowledge_base?: boolean;
    knowledge_source_ids?: number[] | null;
}

export interface MaterialResult {
    filename?: string;
    url?: string;
    title?: string | null;
    source_type: string;
    text: string;
    characters: number;
    truncated: boolean;
}

export interface AcceptResult {
    status: string;
    lesson: { id: number; title: string; type: string };
    section: { id: number; title: string } | null;
    course: { id: number; title: string; contentFormat: string };
}

export const isJobRunning = (status: JobStatus): boolean => status === 'pending' || status === 'processing';

export const aiGeneratorService = {
    async createContentJob(input: CreateContentJobInput): Promise<ContentJob> {
        const { data } = await api.post('/ai/generate', input);
        return data;
    },
    async listContentJobs(): Promise<{ jobs: ContentJob[]; quota: QuotaUsage | null }> {
        const { data } = await api.get('/ai/generate');
        return data;
    },
    async getContentJob(id: number): Promise<ContentJob> {
        const { data } = await api.get(`/ai/generate/${id}`);
        return data;
    },
    async previewBlocks(jobId: number, contentId: number): Promise<{ title: string; completionRule: string; blocks: Block[] }> {
        const { data } = await api.get(`/ai/generate/${jobId}/contents/${contentId}/blocks`);
        return data;
    },
    async acceptContent(jobId: number, contentId: number, body: { course_id: number; section_id?: number; lesson_id?: number; content?: Record<string, unknown> }): Promise<AcceptResult> {
        const { data } = await api.post(`/ai/generate/${jobId}/contents/${contentId}/accept`, body);
        return data;
    },
    async rejectContent(jobId: number, contentId: number): Promise<void> {
        await api.post(`/ai/generate/${jobId}/contents/${contentId}/reject`);
    },
    async createCourseJob(input: CreateCourseJobInput): Promise<CourseJob> {
        const { data } = await api.post('/ai/course-generator', input);
        return data;
    },
    async listCourseJobs(): Promise<{ jobs: CourseJob[]; quota: QuotaUsage | null }> {
        const { data } = await api.get('/ai/course-generator');
        return data;
    },
    async getCourseJob(id: number): Promise<CourseJob> {
        const { data } = await api.get(`/ai/course-generator/${id}`);
        return data;
    },
    async extractFile(file: File): Promise<MaterialResult> {
        const form = new FormData();
        form.append('file', file);
        const { data } = await api.post('/ai/material/extract', form, { headers: { 'Content-Type': 'multipart/form-data' } });
        return data;
    },
    async fetchUrl(url: string): Promise<MaterialResult> {
        const { data } = await api.post('/ai/material/fetch-url', { url });
        return data;
    },
};

/** Human-readable API error (German messages come from the backend). */
export const apiErrorMessage = (error: unknown, fallback: string): string => {
    const data = (error as { response?: { data?: { message?: string; error?: string } } })?.response?.data;
    if (data?.message) return data.message;
    if (data?.error && !/^[a-z_]+$/.test(data.error)) return data.error;
    return fallback;
};

export default aiGeneratorService;
